import React, { useMemo } from 'react';
import AgChartsWrapper from '../AgChartsWrapper';
import type { AgChartOptions } from '../AgChartsWrapper';
import type { ThemeMode } from '../../../../../types';
import {
  DEFAULT_COLORS,
  buildAgChartTheme,
  buildCartesianAxes,
  buildMultiAxes,
  computeAxisLayout,
  computeFixedRange,
  buildEndValueTags,
  buildLegendConfig,
  SERIES_DEFAULTS,
} from '../../../design';

export interface LineChartSeries {
  yKey: string;
  yName: string;
  color?: string;
  fill?: boolean;
  fillOpacity?: number;
  stacked?: boolean;
  markers?: boolean;
  /** Axis ID — series sharing the same ID share the same Y axis.
   *  Omit for single-axis charts. Use different IDs for multi-axis. */
  axis?: string;
  tooltipRenderer?: (params: any) => { title: string; content: string };
}

export interface LineChartProps {
  data: Record<string, unknown>[];
  xKey: string;
  yKey?: string;
  series?: LineChartSeries[];
  colors?: string[];
  title?: string;
  legend?: boolean;
  legendValues?: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  stacked?: boolean;
  markerThreshold?: number;
  showEndValueTags?: boolean;
  endValueFormatter?: (v: number) => string;
  xLabelFormatter?: (params: any) => string;
  /** Y-axis label formatters keyed by axis ID (e.g. { b: axisCompact }) */
  yLabelFormatters?: Record<string, (params: any) => string>;
  /** Shorthand: single Y-axis label formatter (used when only 1 axis) */
  yLabelFormatter?: (params: any) => string;
  hiddenSeries?: Set<string>;
  theme?: ThemeMode;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  xKey,
  yKey,
  series: seriesProp,
  colors: colorsProp,
  title,
  legend,
  legendValues,
  legendPosition = 'bottom',
  stacked: chartStacked,
  markerThreshold = 50,
  showEndValueTags,
  endValueFormatter,
  xLabelFormatter,
  yLabelFormatters,
  yLabelFormatter,
  hiddenSeries,
  theme: themeMode = 'light',
}) => {
  const chartOptions = useMemo(() => {
    const colors = colorsProp || DEFAULT_COLORS;
    const autoMarkers = data.length < markerThreshold;

    const allSeries: LineChartSeries[] = seriesProp
      || (yKey ? [{ yKey, yName: yKey }] : []);

    const colorMap = new Map<string, string>();
    allSeries.forEach((s, i) => {
      colorMap.set(s.yKey, s.color || colors[i % colors.length]);
    });

    const seriesList = hiddenSeries?.size
      ? allSeries.filter((s) => !hiddenSeries.has(s.yKey))
      : allSeries;

    // Compute axis layout from ALL series (before filtering) for stable axes
    const allAxisIds = allSeries.map((s) => s.axis || 'a');
    const uniqueAxisIds = Array.from(new Set(allAxisIds));
    const isMultiAxis = uniqueAxisIds.length > 1;

    // Determine which sides have axes for dynamic padding
    const axisLayout = isMultiAxis ? computeAxisLayout(uniqueAxisIds) : null;
    const hasLeftAxis = isMultiAxis && Array.from(axisLayout!.values()).some((l) => l.position === 'left');
    const hasRightAxis = !isMultiAxis || Array.from(axisLayout!.values()).some((l) => l.position === 'right');
    const agTheme = buildAgChartTheme(colors, themeMode, { hasLeftAxis, hasRightAxis, hasEndValueTags: showEndValueTags });

    // Compute fixed ranges from ALL series (so hiding doesn't rescale)
    const fixedRanges: Record<string, { min: number; max: number }> = {};
    for (const axisId of uniqueAxisIds) {
      const keysOnAxis = allSeries
        .filter((s) => (s.axis || 'a') === axisId)
        .map((s) => s.yKey);
      const hasStackedOnAxis = allSeries
        .some((s) => (s.axis || 'a') === axisId && (s.stacked || (chartStacked && s.fill)));
      fixedRanges[axisId] = computeFixedRange(data, keysOnAxis, hasStackedOnAxis);
    }

    // Build axes
    let axes: Record<string, unknown>;
    if (isMultiAxis) {
      const layout = computeAxisLayout(uniqueAxisIds);
      const fmts: Record<string, (params: any) => string> = { ...yLabelFormatters };
      if (yLabelFormatter && uniqueAxisIds.length > 0 && !fmts[uniqueAxisIds[0]]) {
        fmts[uniqueAxisIds[0]] = yLabelFormatter;
      }
      axes = buildMultiAxes({ layout, mode: themeMode, xLabelFormatter, yLabelFormatters: fmts, fixedRanges });
    } else {
      axes = buildCartesianAxes({
        xLabelFormatter,
        yLabelFormatter: yLabelFormatter || yLabelFormatters?.[uniqueAxisIds[0]],
        yPosition: 'right',
        fixedRange: fixedRanges[uniqueAxisIds[0]],
      });
    }

    // End-value tags via crossLines
    if (showEndValueTags) {
      if (isMultiAxis) {
        const axisLayoutMap = computeAxisLayout(uniqueAxisIds);
        axisLayoutMap.forEach((layout, axisId) => {
          const axisSeries = seriesList
            .filter((s) => (s.axis || 'a') === axisId)
            .map((s) => ({ yKey: s.yKey, yName: s.yName, color: colorMap.get(s.yKey) }));
          if (axisSeries.length === 0) return;
          const isAxisStacked = axisSeries.some((as) => {
            const orig = seriesList.find((s) => s.yKey === as.yKey);
            return orig && (orig.stacked || (chartStacked && orig.fill));
          });
          const crossLines = buildEndValueTags(data, axisSeries, colors, endValueFormatter, layout.position, isAxisStacked);
          if (crossLines.length > 0) {
            const axisObj = axes[layout.key] as Record<string, unknown> | undefined;
            if (axisObj) axisObj.crossLines = crossLines;
          }
        });
      } else {
        const seriesWithColors = seriesList.map((s) => ({
          yKey: s.yKey, yName: s.yName, color: colorMap.get(s.yKey),
        }));
        const hasStacked = seriesList.some((s) => s.stacked || (chartStacked && s.fill));
        const crossLines = buildEndValueTags(data, seriesWithColors, colors, endValueFormatter, 'right', hasStacked);
        if (crossLines.length > 0) {
          const yAxis = axes.y as Record<string, unknown> | undefined;
          if (yAxis) yAxis.crossLines = crossLines;
        }
      }
    }

    // Build series
    const layout = isMultiAxis ? computeAxisLayout(uniqueAxisIds) : null;

    const agSeries = seriesList.map((s) => {
      const color = colorMap.get(s.yKey)!;
      const showMarkers = s.markers ?? autoMarkers;
      const useArea = s.fill === true;
      const isStacked = s.stacked || (chartStacked && useArea);
      const axisId = s.axis || 'a';
      const axisBinding = layout ? { yKeyAxis: layout.get(axisId)!.key } : {};
      const tooltipOpts = s.tooltipRenderer ? { tooltip: { renderer: s.tooltipRenderer } } : {};

      if (useArea) {
        return {
          type: 'area' as const,
          xKey,
          yKey: s.yKey,
          yName: s.yName,
          fill: color,
          stroke: color,
          fillOpacity: s.fillOpacity ?? SERIES_DEFAULTS.area.fillOpacity,
          strokeWidth: SERIES_DEFAULTS.area.strokeWidth,
          marker: { enabled: showMarkers, size: SERIES_DEFAULTS.area.marker.size, fill: color },
          ...tooltipOpts,
          ...(isStacked ? { stacked: true } : {}),
          ...axisBinding,
        };
      }

      return {
        type: 'line' as const,
        xKey,
        yKey: s.yKey,
        yName: s.yName,
        stroke: color,
        strokeWidth: SERIES_DEFAULTS.line.strokeWidth,
        marker: {
          enabled: showMarkers,
          size: SERIES_DEFAULTS.line.marker.size,
          strokeWidth: SERIES_DEFAULTS.line.marker.strokeWidth,
          stroke: SERIES_DEFAULTS.line.marker.stroke,
          fill: color,
        },
        ...tooltipOpts,
        ...axisBinding,
      };
    });

    // Legend
    const legendConfig = buildLegendConfig({
      enabled: legend === true || (legend !== false && seriesList.length > 1),
      position: legendPosition,
      legendValues,
      data,
      series: seriesList,
    });

    return {
      theme: agTheme,
      data,
      title: title ? { text: title } : undefined,
      tooltip: { mode: 'shared' as const },
      legend: legendConfig,
      axes,
      series: agSeries,
    };
  }, [data, xKey, yKey, seriesProp, colorsProp, title, legend, legendValues, legendPosition, chartStacked, markerThreshold, showEndValueTags, endValueFormatter, xLabelFormatter, yLabelFormatter, yLabelFormatters, hiddenSeries, themeMode]);

  return <AgChartsWrapper options={chartOptions as unknown as AgChartOptions} />;
};

export default LineChart;
